import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import type { AxiosError } from 'axios';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useHelpCenterPageDetail } from '/@/hooks/help-center';
import { _ } from '/@/locale';
import { getAppInstance } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import HelpCenterContainer from '../components/container';
import HelpCenterArticleFeedback from './feedback';
import HelpCenterMarkdownContent from './markdown-content';
import { sectionsFromMarkdown } from './markdown-sections';
import HelpCenterRelatedQuestions from './related-questions';
import HelpCenterArticleSections from './sections';

const HelpCenterArticle = ({ slug }: { slug: string }) => {
    const appInstance = useSelector(getAppInstance);
    const navigate = useNavigate();
    const detailSwr = useHelpCenterPageDetail(slug, { enabled: Boolean(slug) });
    const page = detailSwr.data;
    const contentForToc = page ? _(page.content) : '';
    const tocSections = useMemo(() => {
        if (!contentForToc.trim()) {
            return [];
        }
        return sectionsFromMarkdown(contentForToc);
    }, [contentForToc]);
    const loading = detailSwr.isLoading;
    const err = detailSwr.error as AxiosError | undefined;
    const status = err?.response?.status;
    const notFound = Boolean(err && status === 404);
    const loadFailed = Boolean(err && !notFound);

    const goHelpCenter = useCallback(() => {
        navigate(reverse('helpCenter.Home'));
    }, [navigate]);

    const articleBreadcrumbs = useMemo(() => {
        const trail = [{ label: _('Centro de ayuda'), onClick: goHelpCenter }];
        if (page) {
            return [...trail, { label: _(page.title) }];
        }
        return trail;
    }, [goHelpCenter, page]);

    let children: ReactNode;
    if (loading) {
        children = (
            <Stack alignItems='center' py={6}>
                <CircularProgress />
            </Stack>
        );
    } else if (loadFailed) {
        children = (
            <>
                <Typography sx={{ mb: 2 }}>{_('No se pudo cargar el articulo.')}</Typography>
                <Button variant='contained' color='secondary' onClick={() => detailSwr.mutate()}>
                    {_('Reintentar')}
                </Button>
            </>
        );
    } else if (notFound || !page) {
        children = (
            <>
                <Typography sx={{ mb: 2 }}>{_('Este articulo no esta disponible.')}</Typography>
                <Button variant='contained' color='secondary' onClick={goHelpCenter}>
                    {_('Volver al centro de ayuda')}
                </Button>
            </>
        );
    } else {
        const hasSections = tocSections.length > 0;
        const hasRelated = (page.relatedQuestions?.length ?? 0) > 0;

        children = (
            <Grid2 container spacing={4} mb={5}>
                {hasSections && (
                    <Grid2 xs={12} md={3}>
                        <HelpCenterArticleSections sections={tocSections} />
                    </Grid2>
                )}
                <Grid2 xs>
                    <Box sx={{ flex: { lg: '1 1 0' }, minWidth: 0, width: '100%', maxWidth: { lg: 800 } }}>
                        <Box
                            sx={{
                                border: '1px solid #e2e7e9',
                                bgcolor: 'background.paper',
                                overflow: 'hidden',
                            }}
                        >
                            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 3 }}>
                                <HelpCenterMarkdownContent page={page} />
                            </Box>

                            <HelpCenterArticleFeedback appInstanceId={appInstance.id} slug={slug} />
                        </Box>
                    </Box>
                </Grid2>
                {hasRelated && (
                    <Grid2 xs={12} md={3}>
                        <HelpCenterRelatedQuestions page={page} />
                    </Grid2>
                )}
            </Grid2>
        );
    }

    return (
        <HelpCenterContainer breadcrumbs={articleBreadcrumbs}>
            <Stack my={3}>{children}</Stack>
        </HelpCenterContainer>
    );
};

export default HelpCenterArticle;
